import { useState, useEffect, useCallback, useRef } from 'react'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import type { BuzonRemitente } from '@/lib/types'
import { Badge, Button, FormField, Modal } from '@/components/ui'
import { sanitizeHtml } from '@/lib/utils'
import { IconPlus, IconEdit, IconTrash, IconUpload, IconMail } from '@/components/icons/Icons'

/*
  Alta y edición de los remitentes del Buzón de Equipo.

  Lo que esta pantalla tiene que **decir**, no solo tener: `nombre_mostrar` es
  la palanca de identidad real (es literalmente lo que el cliente lee en su
  bandeja) y la foto NO es el avatar que Gmail le muestra al destinatario —
  Gmail pone una letra y eso no depende de nosotros. Sin esas dos aclaraciones
  la pantalla promete algo que no puede cumplir.
*/
const VACIO = {
  nombre: '', apellido: '', nombre_mostrar: '', email: '', cargo: '',
  firma_html: '', activo: true, orden: 0,
}

export default function BuzonRemitentes() {
  const api = useApi()
  const [remitentes, setRemitentes] = useState<BuzonRemitente[]>([])
  const [loading, setLoading] = useState(true)
  const [editando, setEditando] = useState<BuzonRemitente | null>(null)
  const [nuevo, setNuevo] = useState(false)
  const [form, setForm] = useState({ ...VACIO })
  const [guardando, setGuardando] = useState(false)
  const fotoRef = useRef<HTMLInputElement>(null)

  // `api` fuera de las dependencias: useApi() devuelve un objeto nuevo en cada
  // render y esto recargaría en bucle. Mismo criterio que el resto del ERP.
  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      setRemitentes(await api.get<BuzonRemitente[]>('/api/buzon/remitentes'))
    } catch (e: any) {
      showToast(e?.message || 'No se pudieron cargar los remitentes', 'error')
    } finally {
      setLoading(false)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { cargar() }, [cargar])

  function abrirNuevo() {
    setForm({ ...VACIO })
    setEditando(null)
    setNuevo(true)
  }

  function abrirEdicion(r: BuzonRemitente) {
    setForm({
      nombre: r.nombre, apellido: r.apellido ?? '', nombre_mostrar: r.nombre_mostrar,
      email: r.email, cargo: r.cargo ?? '', firma_html: r.firma_html ?? '',
      activo: r.activo, orden: r.orden,
    })
    setEditando(r)
    setNuevo(false)
  }

  function cerrar() {
    setEditando(null)
    setNuevo(false)
  }

  async function guardar() {
    setGuardando(true)
    try {
      if (editando) {
        // `email` no se manda: el backend lo ignora igual, porque ya hay hilos
        // colgando de esa dirección.
        await api.put(`/api/buzon/remitentes/${editando.id}`, {
          nombre: form.nombre, apellido: form.apellido || null,
          nombre_mostrar: form.nombre_mostrar, cargo: form.cargo || null,
          firma_html: form.firma_html || null, activo: form.activo, orden: form.orden,
        })
        showToast('Remitente actualizado', 'success')
      } else {
        await api.post('/api/buzon/remitentes', { ...form, apellido: form.apellido || null })
        showToast('Remitente creado', 'success')
      }
      cerrar()
      await cargar()
    } catch (e: any) {
      showToast(e?.message || 'No se pudo guardar', 'error')
    } finally {
      setGuardando(false)
    }
  }

  async function subirFoto(r: BuzonRemitente, file: File) {
    try {
      const fd = new FormData()
      fd.append('file', file)
      await api.upload(`/api/buzon/remitentes/${r.id}/foto`, fd)
      showToast('Foto actualizada', 'success')
      await cargar()
    } catch (e: any) {
      showToast(e?.message || 'No se pudo subir la foto', 'error')
    }
  }

  async function eliminar(r: BuzonRemitente) {
    if (!confirm(`¿Dar de baja a ${r.nombre_mostrar}?\n\nSi ya tiene hilos, se desactiva en vez de borrarse: los correos ya enviados tienen que seguir diciendo quién los mandó.`)) return
    try {
      const res = await api.del<{ message: string }>(`/api/buzon/remitentes/${r.id}`)
      showToast(res?.message || 'Remitente dado de baja', 'success')
      await cargar()
    } catch (e: any) {
      showToast(e?.message || 'No se pudo dar de baja', 'error')
    }
  }

  const abierto = nuevo || !!editando

  return (
    <>
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <div>
            <h2>Remitentes del Buzón</h2>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: 4 }}>
              Cada persona del equipo manda con su propia dirección y su propia firma.
              Los correos salen por Resend, no desde Gmail.
            </p>
          </div>
          <Button onClick={abrirNuevo}><IconPlus size={14} /> Nuevo remitente</Button>
        </div>

        <div style={{ padding: '0 20px 20px' }}>
          {loading ? (
            <p className="text-muted">Cargando…</p>
          ) : remitentes.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center' }}>
              <IconMail size={26} />
              <p className="text-muted" style={{ marginTop: 8 }}>
                Todavía no hay remitentes. El primero de la lista es el que la bandeja ofrece por defecto.
              </p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Nombre que ve el cliente</th>
                    <th>Dirección</th>
                    <th>Cargo</th>
                    <th>Firma</th>
                    <th>Estado</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {remitentes.map((r) => (
                    <tr key={r.id}>
                      <td>
                        {r.foto_url
                          ? <img src={r.foto_url} alt={r.nombre_mostrar} className="avatar-img" width={32} height={32} />
                          : <span className="text-muted">—</span>}
                      </td>
                      <td><b>{r.nombre_mostrar}</b></td>
                      <td>{r.email}</td>
                      <td>{r.cargo || <span className="text-muted">—</span>}</td>
                      <td>
                        {r.firma_html
                          ? <Badge variant="completado">Cargada</Badge>
                          : <Badge variant="borrador">Sin firma</Badge>}
                      </td>
                      <td>
                        {r.activo
                          ? <Badge variant="activo">Activo</Badge>
                          : <Badge variant="pausado">Inactivo</Badge>}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => abrirEdicion(r)} title="Editar">
                          <IconEdit size={14} />
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          title="Subir foto para la firma"
                          onClick={() => {
                            if (!fotoRef.current) return
                            fotoRef.current.onchange = () => {
                              const f = fotoRef.current?.files?.[0]
                              if (f) subirFoto(r, f)
                              if (fotoRef.current) fotoRef.current.value = ''
                            }
                            fotoRef.current.click()
                          }}
                        >
                          <IconUpload size={14} />
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => eliminar(r)} title="Dar de baja">
                          <IconTrash size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <input ref={fotoRef} type="file" accept="image/png,image/jpeg,image/webp" hidden />
        </div>
      </div>

      <Modal
        open={abierto}
        onClose={cerrar}
        title={editando ? `Editar ${editando.nombre_mostrar}` : 'Nuevo remitente'}
        width={620}
      >
        <div className="form-row">
          <FormField label="Nombre">
            <input className="form-input" value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
          </FormField>
          <FormField label="Apellido">
            <input className="form-input" value={form.apellido}
              onChange={(e) => setForm({ ...form, apellido: e.target.value })} />
          </FormField>
        </div>

        <FormField
          label="Nombre que se muestra"
          hint="Esto es lo que el cliente ve en su bandeja. Es el único control real que tenemos sobre cómo nos lee."
        >
          <input className="form-input" value={form.nombre_mostrar}
            placeholder={[form.nombre, form.apellido].filter(Boolean).join(' ') || 'Teresa Pardo'}
            onChange={(e) => setForm({ ...form, nombre_mostrar: e.target.value })} />
        </FormField>

        <FormField
          label="Dirección"
          hint={editando
            ? 'No se puede cambiar: ya hay hilos colgando de esta dirección.'
            : 'Tiene que ser del subdominio del buzón (@team.1conecta.com).'}
        >
          <input className="form-input" value={form.email} disabled={!!editando}
            placeholder="tpardo@team.1conecta.com"
            onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </FormField>

        <div className="form-row">
          <FormField label="Cargo" hint="Va en la firma.">
            <input className="form-input" value={form.cargo}
              placeholder="Contabilidad"
              onChange={(e) => setForm({ ...form, cargo: e.target.value })} />
          </FormField>
          <FormField label="Orden" hint="Menor primero, en los selectores.">
            <input className="form-input" type="number" value={form.orden}
              onChange={(e) => setForm({ ...form, orden: Number(e.target.value) })} />
          </FormField>
        </div>

        <FormField
          label="Firma (HTML)"
          hint="La foto se referencia por su URL absoluta y SIEMPRE con alt = el nombre: hay clientes que bloquean imágenes remotas y sin alt la firma queda con un hueco."
        >
          <textarea
            className="form-input"
            rows={10}
            value={form.firma_html}
            onChange={(e) => setForm({ ...form, firma_html: e.target.value })}
            style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.5, resize: 'vertical', width: '100%' }}
            placeholder="<table>…</table>"
          />
        </FormField>

        {form.firma_html.trim() && (
          <FormField label="Vista previa de la firma">
            {/* Regla del proyecto: todo dangerouslySetInnerHTML pasa por sanitizeHtml. */}
            <div
              // Blanco fijo: es la vista previa de una firma de CORREO, y el
              // cliente la va a ver sobre el fondo de su bandeja, no sobre el
              // tema del ERP.
              style={{ padding: 14, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', background: '#fff' }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(form.firma_html) }}
            />
          </FormField>
        )}

        <FormField>
          <label className="form-check">
            <input type="checkbox" checked={form.activo}
              onChange={(e) => setForm({ ...form, activo: e.target.checked })} />
            <span>Activo (puede mandar correos)</span>
          </label>
        </FormField>

        <p className="text-muted" style={{ fontSize: 12.5 }}>
          La foto se sube desde la lista, con el botón de subida. Va en la firma, que es donde el
          cliente la ve: el avatar circular de Gmail siempre muestra una letra y eso no depende
          de nosotros.
        </p>

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <Button
            onClick={guardar}
            disabled={guardando || !form.nombre.trim() || (!editando && !form.email.trim())}
          >
            {guardando ? 'Guardando…' : 'Guardar'}
          </Button>
          <button className="btn btn-secondary" onClick={cerrar} disabled={guardando}>Cancelar</button>
        </div>
      </Modal>
    </>
  )
}
